import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../utils/dataInfo';

// Helper functions
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
}).format(amount || 0);
};

const formatNumber = (num) => {
return new Intl.NumberFormat('en-US').format(num || 0);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
});
};

const formatDateShort = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

const getStatusColor = (status) => {
if (!status) return '#757575';

switch (status.toLowerCase()) {
    case 'completed': return '#4caf50';
    case 'pending': return '#ff9800';
    case 'cancelled': return '#f44336';
    case 'quotation': return '#2196f3';
    default: return '#757575';
}
};

const getPaymentMethodColor = (method) => {
if (!method) return '#757575';

switch (method.toLowerCase()) {
    case 'cash': return '#4caf50';
    case 'mobile_money': return '#2196f3';
    case 'bank': return '#673ab7';
    case 'pending': return '#ff9800';
    default: return '#757575';
}
};

const formatOrderNumber = (orderNumber) => {
if (!orderNumber) return 'N/A';
// Remove any existing dashes and format consistently
const clean = orderNumber.replace(/-/g, '');
return clean.replace(/(.{4})/g, '$1-').slice(0, -1);
};

// Generate Excel Report
export const exportOrdersToExcel = async (reportData, filters = {}) => {
try {
    const workbook = XLSX.utils.book_new();
    
    // Main Orders Sheet - Updated to remove servant and branch
    const mainHeaders = [
    'Order No.',
    'Date/Time',
    'Customer',
    'Total Amount',
    'Payment',
    'Status',
    'Order Notes',
    'Payment Notes',
    'Items Count'
    ];

    const mainData = reportData.orders?.map(order => [
    formatOrderNumber(order.order_number),
    formatDate(order.created_at),
    order.customer?.name || 'Walk-in Customer',
    order.total_amount,
    order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING',
    order.order_status ? order.order_status.toUpperCase() : 'PENDING',
    order.order_notes || '',
    order.payment_notes || '',
    order.items?.length || 0
    ]) || [];

    const mainSheetData = [mainHeaders, ...mainData];
    const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);
    XLSX.utils.book_append_sheet(workbook, mainSheet, 'Orders');

    // Summary Sheet - Updated
    const summarySheetData = [
    ['ORDERS REPORT SUMMARY'],
    ['Generated on', new Date().toLocaleString()],
    ['Company', companyInfo.name],
    [''],
    ['Total Orders', formatNumber(reportData.summary.total_orders)],
    ['Total Sales', formatCurrency(reportData.summary.total_sales)],
    ['Total Items Sold', formatNumber(reportData.summary.total_items)],
    ['Average Order Value', formatCurrency(reportData.summary.average_order_value)],
    [''],
    ['FILTERS APPLIED'],
    ['Payment Method', filters.payment_method ? filters.payment_method.toUpperCase() : 'All'],
    ['Order Status', filters.order_status ? filters.order_status.toUpperCase() : 'All'],
    ['Date From', filters.date_from || 'All'],
    ['Date To', filters.date_to || 'All']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Items Sheet
    const allItems = [];
    reportData.orders?.forEach(order => {
    order.items?.forEach(item => {
        allItems.push({
        'Order Number': formatOrderNumber(order.order_number),
        'Item Name': item.food?.name || item.food_name || 'N/A',
        'Item Type': item.food_type_id === 'ki' ? 'Kitchen' : 'Other',
        'Quantity': item.quantity,
        'Unit Price': item.price,
        'Total Amount': item.total_amount
        });
    });
    });

    if (allItems.length > 0) {
    const itemsSheet = XLSX.utils.json_to_sheet(allItems);
    XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Order Items');
    }

    // Top Items Sheet
    if (reportData.summary.top_items && reportData.summary.top_items.length > 0) {
    const topItemsData = reportData.summary.top_items.map(item => ({
        'Item Name': item.food_name,
        'Type': item.food_type === 'ki' ? 'Kitchen' : 'Other',
        'Total Quantity Sold': item.total_quantity,
        'Total Sales': item.total_sales,
        'Order Count': item.order_count,
        'Average Price': item.avg_price
    }));

    const topItemsSheet = XLSX.utils.json_to_sheet(topItemsData);
    XLSX.utils.book_append_sheet(workbook, topItemsSheet, 'Top Items');
    }

    // Set column widths
    const sheets = workbook.SheetNames;
    sheets.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];
    
    for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && cell.v) {
            const cellWidth = String(cell.v).length;
            maxWidth = Math.max(maxWidth, cellWidth);
        }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }
    
    worksheet['!cols'] = colWidths;
    });

    // Generate file name
    const fileName = `orders_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Generate PDF Report - Updated with requested columns and without Executive Summary
export const exportOrdersToPDF = async (reportData, filters = {}) => {
try {
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Orders Report',
    subject: 'Sales Orders and Transaction Report',
    author: companyInfo.name,
    keywords: 'orders, sales, report, transactions',
    creator: companyInfo.name
    });

    // Title Page
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('ORDERS REPORT', 105, 50, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(66, 66, 66);
    doc.text(companyInfo.name, 105, 65, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(97, 97, 97);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 75, { align: 'center' });
    
    if (filters.date_from && filters.date_to) {
    doc.text(`Period: ${formatDateShort(filters.date_from)} to ${formatDateShort(filters.date_to)}`, 105, 82, { align: 'center' });
    }

    // Main Orders Table - Start directly with orders list
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('ORDERS LIST', 20, 20);

    // Prepare table data with exactly the requested columns
    const tableData = reportData.orders?.map(order => [
    formatOrderNumber(order.order_number),
    formatDate(order.created_at),
    order.customer?.name?.substring(0, 20) || 'Walk-in Customer',
    formatCurrency(order.total_amount),
    order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING',
    order.order_status ? order.order_status.toUpperCase() : 'PENDING'
    ]) || [];

    autoTable(doc, {
    startY: 30,
    head: [['Order No.', 'Date/Time', 'Customer', 'Total Amount', 'Payment', 'Status']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [33, 150, 243], textColor: 255, fontSize: 11 },
    columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 44 },
        2: { cellWidth: 50 },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35 },
        5: { cellWidth: 35 }
    },
    didParseCell: (data) => {
        // Style the amount column
        if (data.column.index === 3 && data.cell.raw) {
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Color code payment method
        if (data.column.index === 4 && data.cell.raw) {
        const color = getPaymentMethodColor(data.cell.raw.toLowerCase());
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
        }
        
        // Color code status
        if (data.column.index === 5 && data.cell.raw) {
        const color = getStatusColor(data.cell.raw.toLowerCase());
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
        }
    }
    });

    // Status Distribution
    if (reportData.summary.by_status && Object.keys(reportData.summary.by_status).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('ORDER STATUS DISTRIBUTION', 20, 20);

    const statusData = Object.entries(reportData.summary.by_status).map(([status, data]) => [
        status.toUpperCase(),
        data.count,
        formatCurrency(data.total_amount || 0),
        formatCurrency(data.avg_amount || 0)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Status', 'Count', 'Total Amount', 'Average Amount']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        },
        didParseCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getStatusColor(data.cell.raw.toLowerCase());
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
        }
        }
    });
    }

    // Payment Method Distribution
    if (reportData.summary.by_payment_method && Object.keys(reportData.summary.by_payment_method).length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('PAYMENT METHOD DISTRIBUTION', 20, 20);

    const paymentData = Object.entries(reportData.summary.by_payment_method).map(([method, data]) => [
        method.toUpperCase().replace('_', ' '),
        data.count,
        formatCurrency(data.total_amount || 0),
        formatCurrency(data.avg_amount || 0)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Payment Method', 'Count', 'Total Amount', 'Average Amount']],
        body: paymentData,
        theme: 'grid',
        headStyles: { fillColor: [156, 39, 176], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' }
        },
        didParseCell: (data) => {
        if (data.column.index === 0 && data.cell.raw) {
            const color = getPaymentMethodColor(data.cell.raw.toLowerCase());
            data.cell.styles.textColor = color;
            data.cell.styles.fontStyle = 'bold';
        }
        }
    });
    }

    // Top Items
    if (reportData.summary.top_items && reportData.summary.top_items.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('TOP SELLING ITEMS', 20, 20);

    const topItemsData = reportData.summary.top_items.map(item => [
        item.food_name?.substring(0, 25) || 'Unknown Item',
        item.food_type === 'ki' ? 'Kitchen' : 'Other',
        formatNumber(item.total_quantity),
        formatCurrency(item.total_sales),
        formatCurrency(item.avg_price)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Item Name', 'Type', 'Quantity Sold', 'Total Sales', 'Avg Price']],
        body: topItemsData,
        theme: 'striped',
        headStyles: { fillColor: [156, 39, 176], textColor: 255 },
        columnStyles: {
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
        }
    });
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(companyInfo.name, 20, doc.internal.pageSize.height - 10);
    }

    const fileName = `orders_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Generate CSV Report - Updated
export const exportOrdersToCSV = async (reportData, filters = {}) => {
try {
    const csvLines = [];
    
    // Header
    csvLines.push('ORDERS REPORT');
    csvLines.push(`Generated on: ${new Date().toLocaleString()}`);
    csvLines.push(`Company: ${companyInfo.name}`);
    csvLines.push('');
    
    // Summary
    csvLines.push('SUMMARY');
    csvLines.push('Metric,Value');
    csvLines.push(`Total Orders,${reportData.summary.total_orders}`);
    csvLines.push(`Total Sales,${formatCurrency(reportData.summary.total_sales)}`);
    csvLines.push(`Total Items Sold,${reportData.summary.total_items}`);
    csvLines.push(`Average Order Value,${formatCurrency(reportData.summary.average_order_value)}`);
    csvLines.push('');
    
    // Main Data - Updated columns
    csvLines.push('ORDERS DETAILS');
    csvLines.push('Order No.,Date/Time,Customer,Total Amount,Payment,Status,Order Notes,Payment Notes,Items Count');
    
    reportData.orders?.forEach(order => {
    csvLines.push([
        `"${formatOrderNumber(order.order_number)}"`,
        `"${formatDate(order.created_at)}"`,
        `"${(order.customer?.name || 'Walk-in Customer').replace(/"/g, '""')}"`,
        order.total_amount,
        `"${order.payment_method ? order.payment_method.toUpperCase().replace('_', ' ') : 'PENDING'}"`,
        `"${order.order_status ? order.order_status.toUpperCase() : 'PENDING'}"`,
        `"${(order.order_notes || '').replace(/"/g, '""')}"`,
        `"${(order.payment_notes || '').replace(/"/g, '""')}"`,
        order.items?.length || 0
    ].join(','));
    });
    
    // Items Data
    if (reportData.orders?.some(o => o.items?.length > 0)) {
    csvLines.push('');
    csvLines.push('ORDER ITEMS');
    csvLines.push('Order No.,Item Name,Item Type,Quantity,Unit Price,Total Amount');
    
    reportData.orders.forEach(order => {
        order.items?.forEach(item => {
        csvLines.push([
            `"${formatOrderNumber(order.order_number)}"`,
            `"${(item.food?.name || item.food_name || 'N/A').replace(/"/g, '""')}"`,
            item.food_type_id === 'ki' ? 'Kitchen' : 'Other',
            item.quantity,
            item.price,
            item.total_amount
        ].join(','));
        });
    });
    }

    // Create CSV file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `orders_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Quick PDF Export - Updated
export const exportOrdersQuickPDF = async (reportData) => {
try {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Orders Quick Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    let y = 40;
    
    const quickSummary = [
    `Total Orders: ${formatNumber(reportData.summary.total_orders)}`,
    `Total Sales: ${formatCurrency(reportData.summary.total_sales)}`,
    `Total Items Sold: ${formatNumber(reportData.summary.total_items)}`,
    `Average Order Value: ${formatCurrency(reportData.summary.average_order_value)}`
    ];
    
    quickSummary.forEach(line => {
    doc.text(line, 20, y);
    y += 10;
    });
    
    y += 10;
    
    // Recent Orders
    if (reportData.orders?.length > 0) {
    doc.text('Recent Orders:', 20, y);
    y += 10;
    
    reportData.orders.slice(0, 10).forEach(order => {
        const line = `• ${formatOrderNumber(order.order_number)}: ${formatCurrency(order.total_amount)} (${order.order_status}) - ${order.customer?.name || 'Walk-in'}`;
        if (line.length > 60) {
        doc.text(line.substring(0, 60), 25, y);
        y += 7;
        doc.text(line.substring(60), 25, y);
        } else {
        doc.text(line, 25, y);
        }
        y += 7;
    });
    }
    
    const fileName = `orders_quick_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Quick PDF export failed:', error);
    throw new Error('Failed to export quick PDF: ' + error.message);
}
};